/**
 * Onboarding Analytics Layout
 *
 * Sub-layout with tab navigation for onboarding analytics.
 * Follows the FeatureAnalyticsLayout pattern exactly.
 * 4 tabs: Users (default), Orgs, Funnels, Trends.
 */

import { NavLink, Outlet, Navigate, useLocation } from 'react-router-dom';
import {
  FunnelIcon,
  ArrowTrendingUpIcon,
  BuildingOfficeIcon,
  UserGroupIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';

const tabs = [
  { name: 'Users', to: 'users', icon: UserGroupIcon },
  { name: 'Orgs', to: 'orgs', icon: BuildingOfficeIcon },
  { name: 'Funnels', to: 'funnels', icon: FunnelIcon },
  { name: 'Trends', to: 'trends', icon: ArrowTrendingUpIcon },
  { name: 'Personal Groups', to: 'personal-groups', icon: SparklesIcon },
];

export default function OnboardingLayout() {
  const location = useLocation();

  // Redirect /onboarding to /onboarding/users (default tab)
  if (
    location.pathname === '/onboarding' ||
    location.pathname === '/onboarding/'
  ) {
    return <Navigate to="/onboarding/users" replace />;
  }

  return (
    <div className="space-y-0">
      {/* Header */}
      <div className="bg-gradient-to-r from-deep-navy to-storm-blue text-white rounded-xl mb-6">
        <div className="px-6 py-6">
          <div className="flex items-center gap-3">
            <FunnelIcon className="h-8 w-8" />
            <div>
              <h2 className="text-2xl font-bold">Onboarding Analytics</h2>
              <p className="text-blue-200 text-sm mt-0.5">
                User acquisition funnels, org setup progress, and activation tracking
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm mb-6 sticky top-[52px] z-10">
        <nav className="flex gap-1 overflow-x-auto p-2" aria-label="Onboarding tabs">
          {tabs.map((tab) => (
            <NavLink
              key={tab.name}
              to={tab.to}
              className={({ isActive }) =>
                `flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors min-h-[44px] ${
                  isActive
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-gray-600 hover:bg-gray-100'
                }`
              }
            >
              <tab.icon className="h-4 w-4" />
              {tab.name}
            </NavLink>
          ))}
        </nav>
      </div>

      {/* Page Content */}
      <Outlet />
    </div>
  );
}
